// src/pages/Departments.jsx
import React, { useEffect, useState } from "react";
import {
  getDepartments,
  createDepartment,
  updateDepartment,
} from "../api/departmentService";

import "../styles/Departments.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    code: "",
  });

  useEffect(() => {
    const cached = localStorage.getItem("departments");
    if (cached) setDepartments(JSON.parse(cached));

    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const data = await getDepartments({ fields: "id,name,code" });
      setDepartments(data);
      localStorage.setItem("departments", JSON.stringify(data));
    } catch (err) {
      toast.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDepartment = async () => {
    if (!formData.name || !formData.code) {
      toast.warning("⚠️ Please enter both Department Name and Code");
      return;
    }

    setSaving(true);
    try {
      if (editingDepartment) {
        await updateDepartment(editingDepartment.id, formData);
        toast.success("✅ Department updated successfully!");
        setEditingDepartment(null);
      } else {
        await createDepartment(formData);
        toast.success("✅ Department created successfully!");
      }

      setFormData({ name: "", code: "" });
      loadDepartments();
    } catch (err) {
      console.error(err);
      toast.error("❌ Failed to save department");
    } finally {
      setSaving(false);
    }
  };

  const handleEditDepartment = (dept) => {
    setEditingDepartment(dept);
    setFormData({
      name: dept.name,
      code: dept.code,
    });
  };

  return (
    <div className="add-lesson-container">
      {/* Header */}
      <div className="section-card">
        <h2>🏫 Department Management</h2>
      </div>

      {/* Create / Edit Form */}
      <div className="section-card">
        {editingDepartment && (
          <div className="editing-banner">
            ✏️ Editing Department: <strong>{editingDepartment.name}</strong>
          </div>
        )}

        <h3>{editingDepartment ? "✏️ Edit Department" : "➕ Add Department"}</h3>

        <div className="lesson-form">
          <input
            type="text"
            placeholder="Department Name"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
          />
          <input
            type="text"
            placeholder="Department Code"
            value={formData.code}
            onChange={(e) => handleChange("code", e.target.value)}
          />
        </div>

        <button
          className="animated-save-btn"
          onClick={handleSaveDepartment}
          disabled={saving || !formData.name || !formData.code}
        >
          {saving ? (
            <span className="loader"></span>
          ) : editingDepartment ? (
            "🔄 Update Department"
          ) : (
            "💾 Save Department"
          )}
        </button>
      </div>

      {/* Department List */}
      <div className="section-card">
        <div className="unit-header">
          <h3>📋 All Departments ({departments.length})</h3>
          <input
            type="text"
            placeholder="🔍 Search Department..."
            className="search-input"
            onChange={(e) => setSearch(e.target.value.toLowerCase())}
          />
        </div>

        {/* Loader */}
        {loading ? (
          <div className="loader-wrapper">
            <div className="spinner"></div>
            <p className="loader-text">Loading departments...</p>
          </div>
        ) : (
          <table className="programs-table">
            <thead>
              <tr>
                <th>#</th>
                <th>ID</th>
                <th>Name</th>
                <th>Code</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {departments
                .filter((d) => d.name.toLowerCase().includes(search))
                .map((dept, idx) => (
                  <tr key={dept.id}>
                    <td>{idx + 1}</td>
                    <td>{dept.id}</td>
                    <td>{dept.name}</td>
                    <td>{dept.code}</td>
                    <td className="table-actions">
                      <button
                        className="edit-btn"
                        onClick={() => handleEditDepartment(dept)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default Departments;
